import { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, FileText, Receipt, CreditCard, Users, Settings, AlertCircle, RefreshCw } from 'lucide-react';
import apiClient from '../api/client';
import TablePagination from '../components/TablePagination';

// Map EntityType → icon + colour
const ENTITY_META = {
  Invoice:         { icon: FileText,   color: 'text-blue-600',  bg: 'bg-blue-100'  },
  InvoiceApproval: { icon: FileText,   color: 'text-indigo-600', bg: 'bg-indigo-100' },
  Expense:         { icon: Receipt,    color: 'text-green-600', bg: 'bg-green-100' },
  Payment:         { icon: CreditCard, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  User:            { icon: Users,      color: 'text-purple-600', bg: 'bg-purple-100' },
  Vendor:          { icon: Users,      color: 'text-orange-600', bg: 'bg-orange-100' },
};

function entityMeta(entityType) {
  return ENTITY_META[entityType] || { icon: Activity, color: 'text-gray-500', bg: 'bg-gray-100' };
}

function actionBadge(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('creat') || a.includes('submit') || a.includes('upload'))
    return 'bg-green-100 text-green-700';
  if (a.includes('approv'))
    return 'bg-blue-100 text-blue-700';
  if (a.includes('reject') || a.includes('delet'))
    return 'bg-red-100 text-red-700';
  if (a.includes('update') || a.includes('edit'))
    return 'bg-yellow-100 text-yellow-700';
  return 'bg-gray-100 text-gray-600';
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const diffMin = Math.floor((Date.now() - d) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ENTITY_TYPES = ['Invoice', 'InvoiceApproval', 'Expense', 'Payment', 'User', 'Vendor'];
const ACTIONS = ['Created', 'Updated', 'Approved', 'Rejected', 'Submitted', 'Deleted', 'Uploaded', 'Posted'];

export default function ActivityFeedPage() {
  const [events, setEvents]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [accessLevel, setAccessLevel] = useState('own');
  const [filters, setFilters]     = useState({ entityType: '', action: '', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.action)     params.action     = filters.action;
      if (filters.startDate)  params.startDate  = filters.startDate;
      if (filters.endDate)    params.endDate    = filters.endDate;

      const res = await apiClient.get('/activity-feed', { params });
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setAccessLevel(res.data.accessLevel || 'own');
    } catch (err) {
      console.error('Activity feed error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  function resetFilters() {
    setFilters({ entityType: '', action: '', startDate: '', endDate: '' });
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-blue-600" />
            Activity Feed
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {accessLevel === 'admin'      && 'All company activity'}
            {accessLevel === 'department' && 'Activity for your departments'}
            {accessLevel === 'own'        && 'Your personal activity'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${
              showFilters || hasFilters
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {hasFilters && (
              <span className="ml-1 bg-blue-600 text-white rounded-full text-[10px] px-1.5 py-0.5">
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setPage(1); load(); }}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={filters.entityType}
                onChange={e => { setFilters(f => ({ ...f, entityType: e.target.value })); setPage(1); }}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All types</option>
                {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={e => { setFilters(f => ({ ...f, action: e.target.value })); setPage(1); }}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All actions</option>
                {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={e => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(1); }}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={e => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(1); }}
                className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {hasFilters && (
            <button onClick={resetFilters} className="mt-3 text-xs text-red-600 hover:text-red-800 transition-colors">
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* Event list */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            Loading activity…
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400 gap-2">
            <Activity className="w-10 h-10 text-gray-300" />
            <p className="text-sm">No activity found</p>
            {hasFilters && (
              <button onClick={resetFilters} className="text-xs text-blue-600 hover:underline mt-1">Clear filters</button>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {events.map(ev => {
              const { icon: Icon, color, bg } = entityMeta(ev.EntityType);
              return (
                <li key={ev.AuditEventID} className="flex items-start gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                  <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {ev.ActorFullName || ev.ActorUsername || 'System'}
                      </span>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${actionBadge(ev.Action)}`}>
                        {ev.Action}
                      </span>
                      <span className="text-sm text-gray-500">{ev.EntityType}</span>
                    </div>
                    {ev.ChangesSummary && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{ev.ChangesSummary}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 mt-1">
                    {timeAgo(ev.CreatedAt)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {/* Pagination */}
        {!loading && total > LIMIT && (
          <TablePagination page={page} pageSize={LIMIT} total={total} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
